import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { StatusPill } from '@/components/ui/StatusPill'
import { useApplyToJob } from '@/features/applications/hooks/useApplications'
import { useStudentJob } from '@/features/jobs/hooks/useJobs'
import { getErrorMessage } from '@/utils/errors'
import { formatCurrency, formatDate, formatMonthsAsYears, humanizeEnum } from '@/utils/format'

export function JobDetailPage() {
  const { jobId } = useParams<{ jobId: string }>()
  const navigate = useNavigate()
  const id = Number(jobId)
  const { data: job, isPending, isError } = useStudentJob(id)
  const applyToJob = useApplyToJob()
  const [apiError, setApiError] = useState<unknown>(null)
  const [applied, setApplied] = useState(false)
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [answerErrors, setAnswerErrors] = useState<Record<string, boolean>>({})

  if (isPending) return <p className="text-muted my-4">Loading job…</p>
  if (isError || !job) {
    return (
      <div className="my-4">
        <EmptyState heading="Job not found" message="This job may have been closed or removed." />
      </div>
    )
  }

  const location = [job.locationCity, job.locationState, job.locationCountry]
    .filter(Boolean)
    .join(', ')

  // Server-derived, so this stays correct across reloads/re-navigation —
  // `applied` alone (component state) used to be the only signal, which
  // reset on every fresh page load and let the button show "Apply Now"
  // again for a job already applied to, triggering the backend's
  // already-applied error on the second click.
  const hasApplied = job.hasApplied || applied

  const handleApply = () => {
    const missing = job.screeningQuestions.filter(
      (q) => q.isRequired && !answers[String(q.id)]?.trim(),
    )
    if (missing.length > 0) {
      setAnswerErrors(Object.fromEntries(missing.map((q) => [String(q.id), true])))
      return
    }

    setApiError(null)
    applyToJob.mutate(
      { jobId: id, answers },
      {
        onSuccess: () => setApplied(true),
        onError: (error) => setApiError(error),
      },
    )
  }

  return (
    <>
      <Card className="mb-3 mt-4">
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            {job.organizationLogoUrl ? (
              <img
                src={job.organizationLogoUrl}
                alt={job.organizationName}
                width={56}
                height={56}
                className="rounded"
              />
            ) : (
              <span className="avatar avatar-lg avatar-rounded bg-primary-transparent">
                {job.organizationName.charAt(0).toUpperCase()}
              </span>
            )}
            <div>
              <p className="fs-18 fw-semibold mb-0">{job.title}</p>
              <p className="text-muted mb-0">{job.organizationName}</p>
            </div>
          </div>
          {hasApplied ? (
            <StatusPill status={job.applicationStatus ?? 'applied'} />
          ) : (
            <Button loading={applyToJob.isPending} onClick={handleApply}>
              Apply Now
            </Button>
          )}
        </div>

        {Boolean(apiError) && (
          <div className="alert alert-danger mt-3" role="alert">
            {getErrorMessage(apiError)}
            {getErrorMessage(apiError).includes('Complete your profile') && (
              <>
                {' '}
                <Link to="/profile">Go to your profile</Link>.
              </>
            )}
          </div>
        )}
        {applied && (
          <div className="alert alert-success mt-3" role="alert">
            Application submitted!{' '}
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={() => void navigate('/applications')}
            >
              View your applications
            </button>
          </div>
        )}

        <div className="d-flex flex-wrap gap-2 mt-3">
          {location && <span className="badge bg-light text-dark border">{location}</span>}
          <span className="badge bg-light text-dark border">{humanizeEnum(job.workMode)}</span>
          <span className="badge bg-light text-dark border">
            {humanizeEnum(job.employmentType)}
          </span>
          {job.applicationDeadline && (
            <span className="badge bg-warning-transparent">
              Apply by {formatDate(job.applicationDeadline)}
            </span>
          )}
        </div>
      </Card>

      <div className="row g-3">
        <div className="col-xl-8">
          <Card title="Description">
            <p style={{ whiteSpace: 'pre-wrap' }}>{job.description}</p>
          </Card>

          {job.screeningQuestions.length > 0 && (
            <Card title="Screening Questions" className="mt-3">
              {hasApplied ? (
                <>
                  <p className="text-muted fs-13">
                    The recruiter attached these questions to this posting:
                  </p>
                  <ol>
                    {job.screeningQuestions.map((q) => (
                      <li key={q.id} className="mb-1">
                        {q.questionText} {q.isRequired && <span className="text-danger">*</span>}
                      </li>
                    ))}
                  </ol>
                </>
              ) : (
                <>
                  <p className="text-muted fs-13">
                    Please answer these before applying:
                  </p>
                  <div className="d-flex flex-column gap-3">
                    {job.screeningQuestions.map((q) => {
                      const key = String(q.id)
                      const label = (
                        <>
                          {q.questionText} {q.isRequired && <span className="text-danger">*</span>}
                        </>
                      )
                      const onChange = (value: string) => {
                        setAnswers((prev) => ({ ...prev, [key]: value }))
                        if (answerErrors[key]) {
                          setAnswerErrors((prev) => ({ ...prev, [key]: false }))
                        }
                      }

                      const isSelect =
                        q.questionType === 'yes_no' ||
                        q.questionType === 'multiple_choice' ||
                        q.questionType === 'dropdown'
                      const selectOptions =
                        q.questionType === 'yes_no'
                          ? ['Yes', 'No']
                          : q.options

                      return (
                        <div key={q.id}>
                          <label htmlFor={`screening-q-${q.id}`} className="form-label fs-13 fw-medium">
                            {label}
                          </label>
                          {isSelect ? (
                            <select
                              id={`screening-q-${q.id}`}
                              className={`form-select ${answerErrors[key] ? 'is-invalid' : ''}`}
                              value={answers[key] ?? ''}
                              onChange={(e) => onChange(e.target.value)}
                            >
                              <option value="">Select…</option>
                              {selectOptions.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              id={`screening-q-${q.id}`}
                              type="text"
                              className={`form-control ${answerErrors[key] ? 'is-invalid' : ''}`}
                              value={answers[key] ?? ''}
                              onChange={(e) => onChange(e.target.value)}
                            />
                          )}
                          {answerErrors[key] && (
                            <div className="invalid-feedback">This question is required.</div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
            </Card>
          )}
        </div>

        <div className="col-xl-4">
          <Card title="Job Details">
            <dl className="row mb-0">
              {(job.experienceMinMonths != null || job.experienceMaxMonths != null) && (
                <>
                  <dt className="col-6 fs-13 fw-medium">Experience</dt>
                  <dd className="col-6 fs-13">
                    {formatMonthsAsYears(job.experienceMinMonths)} —{' '}
                    {formatMonthsAsYears(job.experienceMaxMonths)}
                  </dd>
                </>
              )}
              {(job.salaryMin != null || job.salaryMax != null) && (
                <>
                  <dt className="col-6 fs-13 fw-medium">Salary</dt>
                  <dd className="col-6 fs-13">
                    {formatCurrency(job.salaryMin, job.currency ?? 'INR')} —{' '}
                    {formatCurrency(job.salaryMax, job.currency ?? 'INR')}
                  </dd>
                </>
              )}
              {job.skillsRequired.length > 0 && (
                <>
                  <dt className="col-12 fs-13 fw-medium mb-1">Skills</dt>
                  <dd className="col-12">
                    <div className="d-flex flex-wrap gap-1">
                      {job.skillsRequired.map((skill) => (
                        <span key={skill} className="badge bg-light text-dark border">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </dd>
                </>
              )}
              {job.educationRequired.length > 0 && (
                <>
                  <dt className="col-12 fs-13 fw-medium mb-1 mt-2">Education</dt>
                  <dd className="col-12 fs-13">{job.educationRequired.join(', ')}</dd>
                </>
              )}
            </dl>
          </Card>
        </div>
      </div>
    </>
  )
}
